import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Shepherd from 'shepherd.js';
import { offset } from '@floating-ui/dom';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../services/user-service.service';

@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  private _pendingTour: string | null = null;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private userService: UserService
  ) {}

  private get isAiWidgetVisible(): boolean {
    const profile = this.userService.CurrentUserProfile;
    if (!profile?.ShowAI || profile.UserType === 'Student') return false;
    return !!document.querySelector('#tour-ai-widget');
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  setPendingTour(tourId: string): void {
    this._pendingTour = tourId;
  }

  consumePendingTour(): string | null {
    const t = this._pendingTour;
    this._pendingTour = null;
    return t;
  }

  private cancelActiveTour(): void {
    if (Shepherd.activeTour) {
      Shepherd.activeTour.cancel();
    }
  }

  private get tourDefaults() {
    return {
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: false,
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 4,
        floatingUIOptions: {
          middleware: [offset(16)],
        },
      },
    };
  }

  /**
   * Main Menu orientation tour — Home, Messages, Posts, Notifications, App Icons.
   */
  startMainMenuTour(): void {
    this.cancelActiveTour();
    const tour = new Shepherd.Tour(this.tourDefaults);

    const next = () => tour.next();
    const back = () => tour.back();
    const cancel = () => tour.cancel();
    const isMobile = window.innerWidth <= 768;
    const navPlacement = isMobile ? 'top' : 'bottom';

    // The nav row is per-user now, so Messages/Posts/Notifications may not be there. Add only
    // the steps whose anchor is actually on the page, and assign Back by position afterwards so
    // it still works when a middle step is missing. #tour-home is hardcoded and always present.
    const navSteps = [
      { anchor: '#tour-home', key: 'home' },
      { anchor: '#tour-messages', key: 'messages' },
      { anchor: '#tour-posts', key: 'posts' },
      { anchor: '#tour-notifications', key: 'notifications' },
    ].filter(step => !!document.querySelector(step.anchor));

    navSteps.forEach((step, index) => {
      const buttons: any[] = [{ text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true }];
      if (index > 0) {
        buttons.push({ text: this.t('guidedTour.btn.back'), action: back, secondary: true });
      }
      buttons.push({ text: this.t('guidedTour.btn.next'), action: next });

      tour.addStep({
        id: 'main-menu-' + step.key,
        title: this.t(`guidedTour.mainMenu.${step.key}.title`),
        text: this.t(`guidedTour.mainMenu.${step.key}.text`),
        attachTo: { element: step.anchor, on: navPlacement },
        buttons,
      });
    });

    const showAi = this.isAiWidgetVisible;

    tour.addStep({
      id: 'main-menu-app-icons',
      title: this.t('guidedTour.mainMenu.appIcons.title'),
      text: this.t('guidedTour.mainMenu.appIcons.text'),
      attachTo: { element: '#tour-app-icons', on: 'top' },
      buttons: showAi
        ? [
            { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
            { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
            { text: this.t('guidedTour.btn.next'), action: next },
          ]
        : [
            { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
            { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
            { text: this.t('guidedTour.btn.finish'), action: () => tour.complete() },
          ],
    });

    if (showAi) {
      tour.addStep({
        id: 'main-menu-ai-widget',
        title: this.t('guidedTour.mainMenu.aiWidget.title'),
        text: this.t('guidedTour.mainMenu.aiWidget.text'),
        attachTo: { element: '#tour-ai-widget', on: 'top' },
        buttons: [
          { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
          { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
          { text: this.t('guidedTour.btn.finish'), action: () => tour.complete() },
        ],
      });
    }

    tour.start();
  }

  /**
   * Phase 1: runs on the main menu page.
   * Points to the Profile icon and prompts the user to click it.
   * Sets a pending tour so the profile page picks up Phase 2 automatically.
   */
  startProfileTour(isStudent = false): void {
    this.cancelActiveTour();
    // If already on the profile page, skip straight to phase 2
    if (this.router.url.startsWith('/apps/profiles')) {
      this.startProfileEditTour(isStudent);
      return;
    }

    const doStart = () => {
      const tour = new Shepherd.Tour(this.tourDefaults);

      tour.addStep({
        id: 'profile-icon',
        title: this.t('guidedTour.profile.icon.title'),
        text: this.t('guidedTour.profile.icon.text'),
        attachTo: { element: '#tour-profile-icon', on: 'bottom' },
        buttons: [
          { text: this.t('guidedTour.btn.endTour'), action: () => tour.cancel(), secondary: true },
          {
            text: this.t('guidedTour.btn.goToProfile'),
            action: () => {
              this.setPendingTour('profile-setup');
              tour.cancel();
              this.router.navigate(['/apps/profiles']);
            },
          },
        ],
      });

      tour.start();
    };

    if (this.router.url === '/mainmenu') {
      doStart();
    } else {
      this.router.navigate(['/mainmenu']).then(() => {
        setTimeout(doStart, 300);
      });
    }
  }

  /**
   * Phase 2: runs inside the Profile page.
   * Step 1 has no Back because it is the first step after navigating from the main menu.
   */
  startProfileEditTour(isStudent = false): void {
    this.cancelActiveTour();
    const scrollToElement = (el: Element) => {
      const container = document.querySelector('.content') as HTMLElement | null;
      if (!container || !el) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const scrollTop =
        container.scrollTop +
        elRect.top -
        containerRect.top -
        container.clientHeight / 2 +
        elRect.height / 2;
      container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    };

    const tour = new Shepherd.Tour({
      ...this.tourDefaults,
      defaultStepOptions: {
        ...this.tourDefaults.defaultStepOptions,
        scrollTo: true,
        scrollToHandler: scrollToElement,
      },
    });

    const next = () => tour.next();
    const back = () => tour.back();
    const cancel = () => tour.cancel();

    // No Back on this step — it is the first step after navigating from the main menu.
    tour.addStep({
      id: 'avatar-btn',
      title: this.t('guidedTour.profile.avatar.title'),
      text: isStudent
        ? this.t('guidedTour.profile.avatar.studentText')
        : this.t('guidedTour.profile.avatar.text'),
      attachTo: { element: '#tour-avatar-btn', on: 'bottom' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'use-avatar',
      title: this.t('guidedTour.profile.useAvatar.title'),
      text: this.t('guidedTour.profile.useAvatar.text'),
      attachTo: { element: '#tour-use-avatar', on: 'bottom' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'about',
      title: this.t('guidedTour.profile.about.title'),
      text: this.t('guidedTour.profile.about.text'),
      attachTo: { element: '#tour-about', on: 'bottom' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'contact',
      title: this.t('guidedTour.profile.contact.title'),
      text: this.t('guidedTour.profile.contact.text'),
      attachTo: { element: '#tour-contact', on: 'bottom' },
      showOn: () => !!document.querySelector('#tour-contact'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'alt-contact',
      title: this.t('guidedTour.profile.altContact.title'),
      text: this.t('guidedTour.profile.altContact.text'),
      attachTo: { element: '#tour-alt-contact', on: 'bottom' },
      showOn: () => !!document.querySelector('#tour-alt-contact'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'notification-type',
      title: this.t('guidedTour.profile.notificationType.title'),
      text: this.t('guidedTour.profile.notificationType.text'),
      attachTo: { element: '#tour-notification-type', on: 'bottom' },
      showOn: () => !!document.querySelector('#tour-notification-type'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'availability',
      title: this.t('guidedTour.profile.availability.title'),
      text: this.t('guidedTour.profile.availability.text'),
      attachTo: { element: '#tour-availability', on: 'bottom' },
      showOn: () => !!document.querySelector('#tour-availability'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'out-of-office',
      title: this.t('guidedTour.profile.outOfOffice.title'),
      text: this.t('guidedTour.profile.outOfOffice.text'),
      attachTo: { element: '#tour-out-of-office', on: 'bottom' },
      showOn: () => !!document.querySelector('#tour-out-of-office'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'communication-prefs',
      title: this.t('guidedTour.profile.communicationPrefs.title'),
      text: this.t('guidedTour.profile.communicationPrefs.text'),
      attachTo: { element: '#tour-communication-prefs', on: 'top' },
      showOn: () => !!document.querySelector('#tour-communication-prefs'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'digest',
      title: this.t('guidedTour.profile.digest.title'),
      text: this.t('guidedTour.profile.digest.text'),
      attachTo: { element: '#tour-digest', on: 'top' },
      showOn: () => !!document.querySelector('#tour-digest'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'preferences',
      title: this.t('guidedTour.profile.preferences.title'),
      text: this.t('guidedTour.profile.preferences.text'),
      attachTo: { element: '#tour-preferences', on: 'top' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.finish'), action: () => tour.complete() },
      ],
    });

    tour.start();
  }

  /**
   * Navigates to the Messages app and walks through composing and sending a message.
   * On mobile, shows a first step pointing to the FAB so the user taps it themselves,
   * which lets the container properly set showDetails = true.
   * On desktop, navigates directly to the compose view.
   */
  startMessageTour(): void {
    this.cancelActiveTour();
    const isMobile = window.innerWidth <= 768;
    const startComposeTour = () =>
      this.waitForElement('#tour-message-recipient').then(() => this.startMessageComposeTour());

    if (isMobile) {
      const showFabStep = () => {
        const tour = new Shepherd.Tour(this.tourDefaults);
        // No Back on this step — it navigates to the compose view, making Back meaningless.
        tour.addStep({
          id: 'message-fab',
          title: this.t('guidedTour.messages.fab.title'),
          text: this.t('guidedTour.messages.fab.text'),
          attachTo: { element: '#tour-message-fab', on: 'top' },
          buttons: [
            { text: this.t('guidedTour.btn.endTour'), action: () => tour.cancel(), secondary: true },
            {
              text: this.t('guidedTour.btn.next'),
              action: () => {
                tour.cancel();
                document.querySelector<HTMLElement>('#tour-message-fab')?.click();
                startComposeTour();
              },
            },
          ],
        });
        tour.start();
      };

      if (this.router.url.startsWith('/apps/messages')) {
        this.waitForElement('#tour-message-fab').then(showFabStep);
      } else {
        this.router.navigate(['/apps/messages']).then(() =>
          this.waitForElement('#tour-message-fab').then(showFabStep)
        );
      }
    } else {
      if (this.router.url.startsWith('/apps/messages/new')) {
        startComposeTour();
      } else {
        this.router.navigate(['/apps/messages/new']).then(startComposeTour);
      }
    }
  }

  private waitForElement(selector: string): Promise<void> {
    return new Promise(resolve => {
      const check = () => {
        const el = document.querySelector(selector);
        if (el && el.getBoundingClientRect().width > 0) {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  startMessageComposeTourWhenReady(): void {
    this.cancelActiveTour();
    this.waitForElement('#tour-message-recipient').then(() => this.startMessageComposeTour());
  }

  startMessageComposeTour(): void {
    const tour = new Shepherd.Tour(this.tourDefaults);

    const cancel = () => tour.cancel();
    const next = () => tour.next();
    const back = () => tour.back();

    // No Back on this step — it is the first step after navigating to the compose view.
    tour.addStep({
      id: 'message-recipient',
      title: this.t('guidedTour.messages.recipient.title'),
      text: this.t('guidedTour.messages.recipient.text'),
      attachTo: { element: '#tour-message-recipient', on: 'bottom' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'message-input',
      title: this.t('guidedTour.messages.input.title'),
      text: this.t('guidedTour.messages.input.text'),
      attachTo: { element: '#tour-message-input', on: 'top' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'message-toolbar',
      title: this.t('guidedTour.messages.toolbar.title'),
      text: this.t('guidedTour.messages.toolbar.text'),
      attachTo: { element: '#tour-message-toolbar', on: 'top' },
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    const isMobile = window.innerWidth <= 768;
    tour.addStep({
      id: 'message-send',
      title: this.t('guidedTour.messages.send.title'),
      text: this.t('guidedTour.messages.send.text'),
      attachTo: { element: isMobile ? '#tour-message-send-mobile' : '#tour-message-send', on: 'top' },
      buttons: isMobile
        ? [
            { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
            { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
            { text: this.t('guidedTour.btn.finish'), action: () => tour.complete() },
          ]
        : [
            { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
            { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
            {
              text: this.t('guidedTour.btn.next'),
              action: () => {
                this.router.navigate(['/apps/messages']).then(() => next());
              },
            },
          ],
    });

    if (!isMobile) {
    tour.addStep({
      id: 'message-direct-messages',
      title: this.t('guidedTour.messages.directMessages.title'),
      text: this.t('guidedTour.messages.directMessages.text'),
      attachTo: { element: '#tour-direct-messages', on: 'bottom' },
      beforeShowPromise: () => this.waitForElement('#tour-direct-messages'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'message-post-replies',
      title: this.t('guidedTour.messages.postReplies.title'),
      text: this.t('guidedTour.messages.postReplies.text'),
      attachTo: { element: '#tour-post-replies', on: 'bottom' },
      beforeShowPromise: () => this.waitForElement('#tour-post-replies'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.next'), action: next },
      ],
    });

    tour.addStep({
      id: 'message-actions',
      title: this.t('guidedTour.messages.actions.title'),
      text: this.t('guidedTour.messages.actions.text'),
      attachTo: { element: '#tour-message-actions', on: 'bottom' },
      beforeShowPromise: () => this.waitForElement('#tour-message-actions'),
      buttons: [
        { text: this.t('guidedTour.btn.endTour'), action: cancel, secondary: true },
        { text: this.t('guidedTour.btn.back'), action: back, secondary: true },
        { text: this.t('guidedTour.btn.finish'), action: () => tour.complete() },
      ],
    });
    } // end if (!isMobile)

    tour.start();
  }
}
